import { useEffect, type RefObject } from "react";

/**
 * Video-playback keyboard shortcuts for Coach Review's video
 * surface — ports the playback half of legacy
 * `js/coaching/review.js:_handleCoachReviewShortcut`.
 *
 *   Space / K         play ⇄ pause
 *   J                 −5s   (`L` is loop in Coach Review v2 —
 *                            see `useReviewKeyboardShortcuts`)
 *   ArrowLeft / Right −1s / +1s   (Shift → ∓10s)
 *
 * These are SEPARATE from `useReviewKeyboardShortcuts` (the tool-
 * arming letters V/A/L/Z/T/F/...). No key overlaps — the tool hook
 * only handles letter keys, this one handles arrows + space + J/L —
 * but they're split because they have different concerns: the tool
 * hook drives the review-editor store; this one drives the
 * `<video>` element directly. `L` is the one shared letter, but in
 * the tool hook `L` = line (a tactical-only tool, no-op on the
 * telestrator surface where video lives), and here `L` = +5s — so
 * on the video surface only this hook's `L` does anything visible.
 *
 * Guards, matching `useReviewKeyboardShortcuts`:
 *   - Body-attribute scope: only fires when Coach Review is the
 *     active surface (`<body data-coach-review-source>` set).
 *   - Video-mode only: skips unless the active source is `video`.
 *   - Modifier-key skip: Cmd / Ctrl / Alt held → skip (browser
 *     shortcuts win). Shift is NOT skipped — it's the ±10s modifier.
 *   - Input-focus skip-guard: skips when focus is in an
 *     `<input>` / `<textarea>` / `<select>` / `[contenteditable]`
 *     (so typing into the composer doesn't scrub the video).
 *   - No-op when the video element isn't mounted yet.
 */

const SEEK_SMALL = 1;
const SEEK_SMALL_SHIFT = 10;
const SEEK_MEDIUM = 5;

export type UseVideoKeyboardShortcutsOptions = {
  /** Ref to the `<video>` element being controlled. */
  videoRef: RefObject<HTMLVideoElement | null>;
  /** Active Coach Review source — shortcuts only fire on "video". */
  source: "video" | "tactical_board";
};

export function useVideoKeyboardShortcuts({
  videoRef,
  source,
}: UseVideoKeyboardShortcutsOptions): void {
  useEffect(() => {
    // Tactical-board mode has no video — don't even bind.
    if (source !== "video") return;

    const clampSeek = (video: HTMLVideoElement, delta: number) => {
      const max = Number.isFinite(video.duration) ? video.duration : Infinity;
      video.currentTime = Math.min(max, Math.max(0, video.currentTime + delta));
    };

    const handler = (e: KeyboardEvent) => {
      // Body-attribute scope — same gate as the tool-shortcut hook.
      if (!document.body.dataset.coachReviewSource) return;
      // Modifier-key skip (Shift is allowed — it's the ±10s mod).
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Input-focus skip-guard.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable
        ) {
          return;
        }
      }
      const video = videoRef.current;
      if (!video) return;

      switch (e.key) {
        case " ":
        case "k":
        case "K":
          e.preventDefault();
          if (video.paused) void video.play().catch(() => {});
          else video.pause();
          return;
        case "j":
        case "J":
          e.preventDefault();
          clampSeek(video, -SEEK_MEDIUM);
          return;
        // `L` no longer seeks +5s here — the Coach Review v2 keyboard
        // table reassigns `L` to "toggle loop", handled by
        // `useReviewKeyboardShortcuts`. Leaving the +5s case in would
        // double-fire (both hooks listen on `window`). Forward 5s is
        // still reachable via `→` (1s) / `Shift+→` (10s).
        case "ArrowLeft":
          e.preventDefault();
          clampSeek(video, e.shiftKey ? -SEEK_SMALL_SHIFT : -SEEK_SMALL);
          return;
        case "ArrowRight":
          e.preventDefault();
          clampSeek(video, e.shiftKey ? SEEK_SMALL_SHIFT : SEEK_SMALL);
          return;
        default:
          return;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [videoRef, source]);
}
